import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const VictoriaFraserview = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:About Victoria Fraserview Paragraph">
          Victoria-Fraserview is a culturally rich neighborhood located on{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>
          ’s south slope, stretching from East 41st Avenue down to the Fraser
          River, and bordered by Knight Street to the west and Elliott
          Street/Vivian Drive to the east. This area is predominantly
          residential, featuring a mix of single-family homes and newer
          condominium and townhouse developments, especially near the Fraser
          River. The community is known for its multicultural diversity, with a
          significant Indo-Canadian population, making it a vibrant hub for
          international cuisine, fresh produce markets, and specialty shops
          along Victoria Drive.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,600 - $2,000',
          bed2: '$2,200 - $2,600',
          bed3: '$2,750 - $3,150'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Apartment Hunting Victoria Fraserview Paragraph 1">
            Finding an apartment for rent in Victoria-Fraserview, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Victoria Fraserview lifestyle by
            exploring your new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Apartment Hunting Victoria Fraserview Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Apartments for rent in Victoria Fraserview Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Victoria-Fraserview
              </LinkText>{' '}
              cater to a diverse demographic, including young professionals,
              families, and retirees. One-bedroom apartments typically range
              from $1,800 to $2,500 per month, while two-bedroom units can range
              from $2,400 to $3,100 per month. These apartments often feature
              modern amenities and are conveniently located near public
              transportation, shopping centers, and parks.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Houses for rent in Victoria Fraserview Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Victoria-Fraserview
              </LinkText>{' '}
              are ideal for families seeking more space and privacy. These
              properties often feature multiple bedrooms, private gardens, and
              are situated on quiet, tree-lined streets. Renting a house in this
              neighborhood offers the benefit of a suburban lifestyle while
              remaining close to Vancouver’s urban amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Townhouses for rent in Victoria Fraserview Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Victoria-Fraserview
              </LinkText>{' '}
              provide a balance between apartment living and detached homes,
              appealing to families and professionals desiring additional space
              without extensive maintenance. These multi-level homes often
              include modern designs, attached garages, and access to communal
              facilities. Renting a townhouse in this area allows residents to
              enjoy a community-oriented environment with convenient access to
              local amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Basements for rent in Victoria Fraserview Paragraph">
              Basements for rent in Victoria-Fraserview offer affordable housing
              options suitable for students, singles, or couples. These units
              typically include separate entrances, one or two bedrooms, and
              essential amenities. Renting a basement suite in this neighborhood
              provides a cost-effective way to reside in a desirable area with
              easy access to public transportation and local services.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Condos for rent Victoria Fraserview Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Victoria-Fraserview
              </LinkText>
              , attract individuals and small families looking for modern living
              spaces with added amenities such as fitness centers, pools, and
              security services. These units vary in size and style, often
              boasting contemporary designs and proximity to shopping districts
              and parks. Renting a condo in this neighborhood combines the perks
              of upscale living with the convenience of urban accessibility.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Rooms for rent in Victoria Fraserview Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Victoria-Fraserview
              </LinkText>{' '}
              are ideal for students or individuals seeking budget-friendly
              accommodation. These rentals typically involve sharing common
              areas like kitchens and living rooms with other tenants. Renting a
              room in this neighborhood offers an affordable living solution in
              a community known for its safety, amenities, and excellent public
              transit links.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Short term rentals in Victoria Fraserview Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Victoria-Fraserview
              </LinkText>{' '}
              cater to tourists, business travelers, or individuals in
              transition. These accommodations range from furnished apartments
              to entire homes, providing flexibility and comfort. Opting for a
              short-term rental in this neighborhood allows guests to experience
              the area’s vibrant culture, access to shopping centers, and
              proximity to major city attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Exploring Victoria Fraserview Paragraph">
          Victoria-Fraserview, nestled in Vancouver’s southern region, is a
          neighborhood that beautifully blends residential tranquility with
          cultural vibrancy. Known for its significant Indo-Canadian community,
          the area boasts a rich tapestry of cultural experiences, particularly
          along{' '}
          <LinkText href="https://victoriadrivebia.com/">
            Victoria Drive
          </LinkText>
          , where one can find an array of ethnic shops, eateries, and markets.
          The neighborhood’s history dates back to the 1860s when settlers were
          drawn to its vast virgin forests along the{' '}
          <LinkText href="https://en.wikipedia.org/wiki/Fraser_River">
            Fraser River
          </LinkText>
          . Today, Victoria-Fraserview is characterized by its single-family
          homes, interspersed with modern condominiums and townhouses,
          especially near the{' '}
          <LinkText href="https://fraserlands.ca/">Fraserlands</LinkText>. The
          community is also home to the historic{' '}
          <LinkText href="https://www.avalondairy.com/">Avalon Dairy</LinkText>,
          established in 1906, which stands as a testament to the area’s
          agricultural roots.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Things To Do Victoria Fraserview Paragraph">
          Victoria-Fraserview offers a variety of activities catering to diverse
          interests. For outdoor enthusiasts, the{' '}
          <LinkText href="https://www.fraserviewgolfclub.com/">
            Fraserview Golf Course
          </LinkText>
          provides a scenic and challenging 18-hole course nestled amidst
          towering trees. The neighborhood’s numerous parks, such as{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/ParkDetail.aspx?inparkid=191">
            Bobolink Park
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=192">
            Fraserview Park
          </LinkText>
          , offer green spaces perfect for picnics, jogging, or leisurely
          strolls. Along the Fraser River, the{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=197">
            Riverfront Park
          </LinkText>
          provides picturesque views and walking trails. Culturally, the area is
          vibrant, with the Victoria Drive corridor offering a plethora of
          ethnic restaurants, markets, and shops, reflecting the community’s
          diverse population. The nearby{' '}
          <LinkText href="https://vancouversbestplaces.com/city-of-vancouver/river-district-vancouver/">
            River District
          </LinkText>{' '}
          also hosts seasonal events, farmers’ markets, and community
          gatherings, making it a hub of activity for residents and visitors
          alike.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Restaurants Victoria Fraserview Paragraph">
        <Paragraph>
          Victoria Fraserview Vancouver offers a range of dining options for
          every taste. Here are two restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://phothaiha.com/">
            Pho Thai HaThe Acorn
          </LinkText>{' '}
          is a beloved spot for authentic Vietnamese cuisine. Renowned for its
          rich and flavorful pho, the restaurant offers a variety of traditional
          dishes in a casual setting. Signature items include the beef noodle
          soup and fresh spring rolls. The cozy ambiance and friendly service
          make it a favorite among locals seeking comfort food. It’s an ideal
          spot for those craving hearty Vietnamese fare.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://newnoveltyrestaurant.com/">
            New Novelty Sweet & Restaurant
          </LinkText>{' '}
          specializes in Indian sweets and savory dishes. Known for its
          delectable samosas and a wide array of traditional desserts.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Transportation Victoria Fraserview Paragraph">
          Getting around Victoria-Fraserview, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Victoria Fraserview are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Victoria Fraserview offers accessible and efficient
          transportation choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.victoria-fraserview-vancouver:Where To Stay Victoria Fraserview Paragraph">
          Accommodation options in Victoria-Fraserview cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Victoria Fraserview itself, its proximity to other parts of Vancouver
          ensures that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Victoria
          Fraserview allows visitors to experience its distinct character while
          remaining conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default VictoriaFraserview
